import { useContext } from 'react';

import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import Page from '../../../components/Page/Page';
import Table from '../../../components/Table/Table';
import CustomerLinkCell from '../../../components/Table/TableAccessories/CustomerLinkCell';
import TableDateTimeFormatter from '../../../components/Table/TableAccessories/TableDateTimeFormatter';
import TableMoneyFormatter from '../../../components/Table/TableAccessories/TableMoneyFormatter';
import { TableContext } from '../../../components/Table/TableContext';
import {
  PAUSE_REASON_LABELS,
  SCHEDULE_KIND_LABELS,
  SCHEDULE_STATUS_OPTIONS,
} from '../../../models/schedules/schedule.model';
import { capitalize } from '../../../utils/utils';
import ScheduleDetailSlider from './ScheduleDetailSlider';

const Schedules = () => (
  <Page title='Hethera | Scheduled payments'>
    <PageLayout>
      <PageHeader title='Scheduled payments' className='no-bottom-padding' />

      <Table
        tableHeader={{
          Code: 'code',
          Customer: 'chat_id',
          Name: 'name',
          Bill: 'bill_type',
          Amount: 'amount',
          Repeats: 'kind',
          'Next payment': 'next_run_date',
          Status: 'status',
          Created: 'created_at',
        }}
        tableDataSourceUrl='api/v1/admin/schedules'
        size={20}
        border
        tableFilterBoxFeatures={{
          showSearchBox: true,
          searchBoxPlaceholder: 'Search by code or phone number',
          rightAttr: [{ type: 'custom-filter', comp: <StatusFilter /> }],
        }}
        cellsCustomComp={{
          chat_id: <CustomerLinkCell />,
          bill_type: <BillCell />,
          amount: <TableMoneyFormatter dataKey='amount' />,
          kind: <KindCell />,
          status: <ScheduleStatusCell />,
          created_at: <TableDateTimeFormatter dataKey='created_at' />,
        }}
        clickRowShowSliderComp={(row) => <ScheduleDetailSlider data={row as any} />}
        hasClickableRow
        emptyStateMsg='No scheduled payments found'
      />
    </PageLayout>
  </Page>
);

export default Schedules;

const BillCell: React.FC<{ data?: Record<string, any> }> = ({ data }) => (
  <>{data ? `${capitalize(String(data.bill_type).replace(/_/g, ' '))} · ${data.service_id}` : '—'}</>
);

const KindCell: React.FC<{ data?: Record<string, any> }> = ({ data }) => (
  <>{data ? SCHEDULE_KIND_LABELS[data.kind] ?? data.kind : '—'}</>
);

// The pill, plus why when it's paused.
const ScheduleStatusCell: React.FC<{ data?: Record<string, any> }> = ({ data }) => (
  <StatusStack>
    <StatusCell data={data} statusKey='status' hasDot />
    {data?.pause_reason && <small>{PAUSE_REASON_LABELS[data.pause_reason] ?? data.pause_reason}</small>}
  </StatusStack>
);

const StatusFilter = () => {
  const tableContext = useContext(TableContext);
  return (
    <Select
      defaultValue=''
      aria-label='Filter by status'
      onChange={(e) => tableContext?.handleSetTableFilterRecord({ status: e.target.value, page: 0 })}
    >
      <option value=''>All statuses</option>
      {SCHEDULE_STATUS_OPTIONS.map((status) => (
        <option key={status} value={status}>
          {capitalize(status)}
        </option>
      ))}
    </Select>
  );
};

const StatusStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  & small {
    font-size: 11px;
    color: #5D665F;
  }
`;

const Select = styled.select`
  border: 1px solid #E2DED3;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: #0E1A13;
  background-color: white;
`;
